import mime from 'mime'
import numeral from 'numeral'

import { getTranslate, globalErrorHandler, request, states } from '@/utils'

/**
 * Возвращает текст ошибки превышения максимального размера файла.
 *
 * Максимальный размер подставляется в единицах, привычных человеку. Если
 * максимальный размер не задан, текст берётся из общего сообщения
 * о слишком большом теле запроса.
 *
 * @param {number|undefined} maxFileSize Максимальный размер файла в байтах.
 * @returns {string} Текст ошибки для показа пользователю.
 */
function getFileMaxSizeText(maxFileSize) {
  if (isMaxFileSizeDefined(maxFileSize)) {
    return getTranslate('errors.validation.types.fileMaxSize', { expected: numeral(maxFileSize).format('0.0b') })
  }

  return getTranslate('errors.client.413')
}

/**
 * Проверяет, что максимальный размер файла задан положительным числом.
 *
 * @param {number|undefined} maxFileSize Максимальный размер файла в байтах.
 * @returns {boolean} Признак заданного ограничения.
 */
function isMaxFileSizeDefined(maxFileSize) {
  if (typeof maxFileSize !== 'number') {
    return false
  }

  return maxFileSize > 0
}

/**
 * Проверяет, что размер файла укладывается в максимальный размер.
 *
 * Ограничение применяется только при заданном максимальном размере.
 *
 * @param {File} file Загружаемый файл.
 * @param {number|undefined} maxFileSize Максимальный размер файла в байтах.
 * @returns {boolean} Признак допустимого размера файла.
 */
function isFileSizeAllowed(file, maxFileSize) {
  if (!isMaxFileSizeDefined(maxFileSize)) {
    return true
  }

  return file.size <= maxFileSize
}

/**
 * Показывает пользователю ошибку загрузки файла.
 *
 * Для статуса 413 показывается сообщение о превышении максимального размера
 * файла, остальные ошибки проходят через общую обработку ошибок запроса.
 *
 * @param {Function} dispatch Функция отправки действий стора.
 * @param {number|undefined} maxFileSize Максимальный размер файла в байтах.
 * @param {object} error Ошибка запроса.
 */
function showUploadFileError(dispatch, maxFileSize, error) {
  if (error?.error === 413) {
    dispatch(
      'notifications/addSnackbar',
      {
        text: getFileMaxSizeText(maxFileSize),
        type: 'error'
      },
      { root: true }
    )

    return
  }

  globalErrorHandler(error)
}

export default {
  namespaced: true,

  state: {
    file: undefined,
    state: undefined,
    error: undefined,

    attributes: {
      isPublic: undefined,
      multiple: undefined,
      name: undefined,
      types: undefined,
      url: undefined,
      method: undefined,
      maxFileSize: undefined
    }
  },

  mutations: {
    setFile(state, value) {
      state.file = value
    },

    setState(state, value) {
      state.state = value
    },

    setError(state, value) {
      state.error = value
    },

    setAttributes(state, value) {
      state.attributes = value
    }
  },

  actions: {
    setFile({ commit }, value) {
      commit('setFile', value)
    },

    setState({ commit }, value) {
      commit('setState', value)
    },

    setError({ commit }, value) {
      commit('setError', value)
    },

    setAttributes({ commit }, value) {
      commit('setAttributes', {
        isPublic: value.isPublic,
        multiple: value.multiple,
        name: value.name,
        types: value.types,
        url: value.url,
        method: value.method,
        maxFileSize: value.maxFileSize
      })
    },

    async uploadFile({ state, commit, dispatch, rootState }, value) {
      try {
        commit('setState', states.loading)

        const params = {}
        const headers = {
          Accept: 'application/json',
          Authorization: rootState.authentication.token
        }

        let body
        if (state.attributes.name && typeof state.attributes.name === 'string') {
          body = new FormData()
          body.append(state.attributes.name, value, value.name)
        } else {
          body = value
          params.filename = value.name

          let type = value.type
          const match = value.name.match(/.*\.(.+)/)
          if (match) {
            type = mime.getType(match[1])
          }

          if (type) {
            headers['Content-Type'] = type
          }
        }

        if (state.attributes.isPublic) {
          params.isPublic = true
        }

        const result = await request.request(
          state.attributes.url,
          {
            method: state.attributes.method,
            body,
            headers
          },
          params
        )

        if (result) {
          commit('setState', states.success)
          commit('setFile', result)
        } else {
          throw new Error('Server did not respond')
        }
      } catch (error) {
        commit('setState', states.error)
        commit('setError', error)
        showUploadFileError(dispatch, state.attributes.maxFileSize, error)
      }
    },

    handler({ state, dispatch }, value) {
      const { target, dataTransfer } = value || {}
      let files = target?.files
      if (dataTransfer) {
        files = dataTransfer?.files
      }
      if (files) {
        for (const file of files) {
          if (isFileSizeAllowed(file, state.attributes.maxFileSize)) {
            dispatch('file/uploadFile', file, { root: true })
          } else {
            dispatch(
              'notifications/addSnackbar',
              {
                text: getFileMaxSizeText(state.attributes.maxFileSize),
                type: 'error'
              },
              { root: true }
            )
          }
        }
      }
    }
  },

  getters: {
    file: state => {
      return state.file
    },
    state: state => {
      return state.state
    },
    error: state => {
      return state.error
    },
    attributes: state => {
      return state.attributes
    }
  }
}
